import React from 'react';
import { Layers, MoreVertical, PowerOff, Star } from 'lucide-react';
import defaultNoteCover from '../assets/note-covers/default-note-cover.svg';
import { t } from '../i18n';
import './NoteCard.css';

const BACKEND_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api$/, '')
  : 'http://localhost:3001';

function resolveCoverUrl(coverUrl) {
  if (!coverUrl) return null;
  if (/^(?:https?:\/\/|blob:|data:)/i.test(coverUrl)) return coverUrl;
  return `${BACKEND_URL}${coverUrl}`;
}

function formatDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return t('dashboard.notes.unknownDate');
  }

  const year = String(date.getFullYear()).slice(-2);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

function NoteCard({
  note,
  onClick,
  onPractice,
  onEdit,
  onMoveMode,
  isMoveEnabled = false,
  onDetail,
  onToggleEnabled,
  onDelete,
  actionLabel,
  controlsDisabled = false,
}) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const optionsAreaRef = React.useRef(null);
  const optionsButtonRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const menuId = React.useId();

  React.useEffect(() => {
    if (!isMenuOpen) return undefined;
    menuRef.current?.querySelector('button:not(:disabled)')?.focus();
    const dismissOutside = (event) => {
      if (!optionsAreaRef.current?.contains(event.target)) setIsMenuOpen(false);
    };
    document.addEventListener('pointerdown', dismissOutside, true);
    return () => document.removeEventListener('pointerdown', dismissOutside, true);
  }, [isMenuOpen]);
  const {
    id,
    title,
    flashcardCount,
    completedCardCount = 0,
    coverUrl,
    createdAt,
    averageGrade,
    averagePracticeCount,
    lastPracticedAt,
    lastStudiedAt,
    isEnabled: rawIsEnabled,
  } = note;
  const isEnabled = rawIsEnabled !== false;
  const resolvedCoverUrl = resolveCoverUrl(coverUrl);
  const safeFlashcardCount = Math.max(0, Number(flashcardCount) || 0);
  const safeCompletedCardCount = Math.min(
    safeFlashcardCount,
    Math.max(0, Number(completedCardCount) || 0)
  );
  const hasCompletedCards = safeCompletedCardCount > 0 && safeFlashcardCount > 0;
  const isFullyCompleted = hasCompletedCards && safeCompletedCardCount >= safeFlashcardCount;
  const completionPercent = safeFlashcardCount > 0
    ? Math.min(100, Math.round((safeCompletedCardCount / safeFlashcardCount) * 100))
    : 0;
  const hasPracticeActivity =
    Boolean(lastPracticedAt || lastStudiedAt) ||
    Number(averagePracticeCount || 0) > 0 ||
    safeCompletedCardCount > 0;
  const isNewNote = safeFlashcardCount > 0 && !hasPracticeActivity;

  const handleCardClick = () => {
    setIsMenuOpen(false);
    if (onClick && typeof onClick === 'function') {
      onClick(id);
    }
  };

  const handleOptionsClick = (event) => {
    event.stopPropagation();
    setIsMenuOpen((prev) => !prev);
  };

  const runMenuAction = (event, action) => {
    event.stopPropagation();
    setIsMenuOpen(false);
    optionsButtonRef.current?.focus();
    action?.(id);
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setIsMenuOpen(false);
      optionsButtonRef.current?.focus();
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const items = Array.from(menuRef.current?.querySelectorAll('button:not(:disabled)') || []);
      const currentIndex = items.indexOf(document.activeElement);
      const nextIndex = event.key === 'Home' ? 0
        : event.key === 'End' ? items.length - 1
          : (currentIndex + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items[nextIndex]?.focus();
    }
  };

  return (
    <div className="note-card-frame">
      <button
        type="button"
        className={`note-card${isMoveEnabled ? ' note-card-move-enabled' : ''}${isEnabled ? '' : ' note-card-disabled'}`}
        data-testid="note-card"
        onClick={handleCardClick}
        disabled={controlsDisabled}
        aria-label={actionLabel || t('dashboard.notes.open', undefined, {
          title, count: flashcardCount, state: t(isEnabled ? 'notes.enabled' : 'notes.disabled'),
        })}
      >
        <span className="note-card-cover">
          <span className="note-card-flags">
            {isNewNote && (
              <span className="note-card-new-flag">{t('dashboard.notes.new')}</span>
            )}
            {!isEnabled && (
              <span className="note-card-disabled-mark" aria-hidden="true">
                <PowerOff size={15} strokeWidth={2.8} />
              </span>
            )}
          </span>

          {resolvedCoverUrl ? (
            <img
              src={resolvedCoverUrl}
              alt={t('dashboard.notes.cover', undefined, { title })}
              className="note-card-cover-image"
              data-testid="note-card-cover-image"
            />
          ) : (
            <img
              src={defaultNoteCover}
              alt=""
              className="note-card-cover-image note-card-default-cover-image"
              data-testid="note-card-placeholder"
              aria-hidden="true"
            />
          )}

          {hasCompletedCards && (
            <span
              className={`note-card-completion${isFullyCompleted ? ' note-card-completion-complete' : ''}`}
              aria-label={t(isFullyCompleted ? 'dashboard.notes.allCompleted' : 'dashboard.notes.someCompleted', undefined, {
                completed: safeCompletedCardCount, count: safeFlashcardCount,
              })}
            >
              {isFullyCompleted ? (
                <span className="note-card-completion-stamp">Completed</span>
              ) : (
                <span className="note-card-completion-line">
                  <span className="note-card-completion-track" aria-hidden="true">
                    <span style={{ width: `${completionPercent}%` }} />
                  </span>
                </span>
              )}
            </span>
          )}
        </span>

        <span className="note-card-info">
          <span className="note-card-title" data-testid="note-card-title">{title}</span>

          <span className="note-card-meta">
            <span className="note-card-count" data-testid="note-card-count">
              <Layers size={12} strokeWidth={2.4} aria-hidden="true" />
              {flashcardCount}
            </span>
            {averageGrade != null && averageGrade > 0 && (
              <span className="note-card-grade">
                <Star size={12} strokeWidth={2.4} aria-hidden="true" />
                {averageGrade.toFixed(1)}
              </span>
            )}
            <span className="note-card-date">{formatDate(createdAt)}</span>
          </span>
        </span>
      </button>
      <div
        ref={optionsAreaRef}
        className="note-card-options-area"
        onBlur={(event) => {
          // Safari may focus a tabindex ancestor (AppMain), or nothing, when a
          // menu button is tapped. Neither means the pointer left the menu.
          // Keep it mounted until click; outside taps have their own handler.
          const nextFocus = event.relatedTarget;
          if (nextFocus
            && !event.currentTarget.contains(nextFocus)
            && !nextFocus.contains?.(event.currentTarget)) {
            setIsMenuOpen(false);
          }
        }}
      >
        <button
          ref={optionsButtonRef}
          type="button"
          className="note-card-options-btn"
          onClick={handleOptionsClick}
          disabled={controlsDisabled}
          aria-label={t('dashboard.notes.openOptions', undefined, { title })}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          aria-controls={isMenuOpen ? menuId : undefined}
          title={t('dashboard.notes.options')}
        >
          <MoreVertical size={18} strokeWidth={2.7} aria-hidden="true" />
        </button>
        {isMenuOpen && (
          <div ref={menuRef} id={menuId} className="note-card-menu" role="menu" aria-label={t('dashboard.notes.optionsFor', undefined, { title })} onKeyDown={handleMenuKeyDown}>
            <button
              type="button"
              role="menuitem"
              onClick={(event) => runMenuAction(event, onPractice)}
              disabled={!isEnabled || controlsDisabled}
              title={!isEnabled ? t('notes.disabledPracticeMessage') : undefined}
            >
              {t('dashboard.notes.practice')}
            </button>
            {onDetail && <button type="button" role="menuitem" disabled={controlsDisabled} onClick={(event) => runMenuAction(event, onDetail)}>{t('vocabulary.details')}</button>}
            <button type="button" role="menuitem" disabled={controlsDisabled} onClick={(event) => runMenuAction(event, onEdit || onClick)}>
              {t('dashboard.notes.edit')}
            </button>
            <button type="button" role="menuitem" disabled={controlsDisabled} onClick={(event) => runMenuAction(event, onMoveMode)}>
              {t('dashboard.notes.move')}
            </button>
            {onToggleEnabled && <button type="button" role="menuitem" disabled={controlsDisabled} onClick={(event) => runMenuAction(event, onToggleEnabled)}>{t(isEnabled ? 'vocabulary.disable' : 'vocabulary.enable')}</button>}
            {onDelete && <button type="button" role="menuitem" disabled={controlsDisabled} onClick={(event) => runMenuAction(event, onDelete)}>{t('vocabulary.delete')}</button>}
          </div>
        )}
      </div>
    </div>
  );
}

export default NoteCard;
